/**
 * Utility function for account data
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright (c) Ircam amplify 2024
 */
import { CompanyConsumption } from '../store/slices/user.slice'

const TierFreeCreditMaxCount = 2
export const OneDayInMs = 1000 * 60 * 60 * 24

/**
 * The type of tier.
 */
export enum TierType {
  Unknown = 'unknown',
  Free = 'free',
  PayAsYouGo = 'pay_as_you_go',
  Subscription = 'subscription',
  Enterprise = 'enterprise',
  EnterpriseSubscription = 'enterprise_subscription',
  EnterprisePayAsYouGo = 'enterprise_pay_as_you_go',
}

/**
 * The type of invoice, distinguishing base and over-package invoices.
 */
export enum InvoiceType {
  Unknown,
  Free,
  PayAsYouGo,
  StarterMonthlyBase,
  StarterMonthlyOverPackage,
  StarterYearlyBase,
  StarterYearlyOverPackage,
  ProMonthlyBase,
  ProMonthlyOverPackage,
  ProYearlyBase,
  ProYearlyOverPackage,
  Enterprise,
}

export enum SubscriptionFrequency {
  Unknown = 'unknown',
  Monthly = 'monthly',
  Yearly = 'yearly',
}

export const AccountUtil = {
  /**
   * Convert a string to a Tier value.
   *
   * @param strValue
   *  The string to convert or an undefined value.
   * @returns The corresponding Tier enum value.
   */
  stringToTier: (strValue: string | undefined): TierType => {
    if (Object.values(TierType).includes(strValue as TierType)) {
      // Cast to Tier, since it's a valid value
      return strValue as TierType
    }

    // Default to Tier.Unknown if not valid
    return TierType.Unknown
  },

  /**
   * Convert a TierType value to a human-readable string.
   *
   * @param tier
   *  The TierType value to convert.
   * @returns
   *  The string.
   */
  humanReadableTier: (tier: TierType | undefined): string => {
    let str = `${TierType.Unknown}`
    if (tier) {
      switch (tier) {
        case TierType.EnterpriseSubscription:
        case TierType.EnterprisePayAsYouGo:
          str = `${TierType.Enterprise}`
        default:
          str = `${tier}`.replace(/_/g, ' ')
      }
    }

    return str
  },

  /**
   * Return true if the given tier is an Enterprise plan.
   */
  isTierEnterprise: (tier: TierType | undefined): boolean => {
    return (
      tier === TierType.Enterprise ||
      tier === TierType.EnterprisePayAsYouGo ||
      tier === TierType.EnterpriseSubscription
    )
  },

  /**
   * Return the Tier value, given a CompanyConsumption data structure.
   *
   * @param companyConsumption
   *  The data structure of type CompanyConsumption or an undefined value.
   * @returns The corresponding Tier enum value.
   */
  getCompanyTier: (
    companyConsumption: CompanyConsumption | undefined
  ): TierType => {
    // Check if the subscription_tier is a valid enum value
    const tierValue = companyConsumption?.subscription_tier
    return AccountUtil.stringToTier(tierValue)
  },

  isCompanyTierEnterprise: (
    companyConsumption: CompanyConsumption | undefined
  ): boolean => {
    const tier = AccountUtil.getCompanyTier(companyConsumption)
    return AccountUtil.isTierEnterprise(tier)
  },

  getCompanySubscriptionCurrentPerUseSubscriptionPriceId: (
    companyConsumption: CompanyConsumption | undefined
  ): string | null | undefined => {
    return companyConsumption?.subscription_tier === TierType.Subscription
      ? companyConsumption?.current_per_use_subscription_price
      : null
  },

  getCompanySubscriptionCurrentBaseSubscriptionPriceId: (
    companyConsumption: CompanyConsumption | undefined
  ): string | null | undefined => {
    return companyConsumption?.subscription_tier === TierType.Subscription
      ? companyConsumption?.current_base_subscription_price
      : null
  },

  /**
   * Returns the list of prices given the currency and the category of prices.
   * @param prices
   *  The complete price data.
   * @param isEuroCurrency
   *  If true, request the prices in euro, else in dollars.
   * @param isStandard
   *  If true, request standard prices, else out package prices.
   * @returns
   */
  getPriceList: (prices: any, isEuroCurrency: boolean, isStandard: boolean) => {
    const prefix = isEuroCurrency ? 'eur' : 'usd'
    const key = isStandard ? `${prefix}_standard` : `${prefix}_out_package`
    return prices[key]
  },

  /**
   * Return the credit consumption of the given CompanyConsumption data
   * structure.
   *
   * @param companyConsumption
   *    The data in a CompanyConsumption interface.
   * @return
   *    For Free account: the number of remaining/available credits.
   *    For other type of account: the numner of
   */
  getConsumption: (companyConsumption: CompanyConsumption | undefined) => {
    if (!companyConsumption) {
      return 0
    }
    const currentTier = AccountUtil.getCompanyTier(companyConsumption)

    switch (currentTier) {
      case TierType.Free:
        return companyConsumption.voucher_balance

      case TierType.PayAsYouGo:
        return companyConsumption.company_balance

      case TierType.Subscription:
        if (
          companyConsumption.current_plan &&
          companyConsumption.company_balance
        ) {
          return (
            companyConsumption.current_plan - companyConsumption.company_balance
          )
        } else {
          return 0
        }

      case TierType.Enterprise:
      case TierType.EnterprisePayAsYouGo:
      case TierType.EnterpriseSubscription:
        return companyConsumption?.company_balance

      case TierType.Unknown:
        return 0
    } // switch()
  },

  /**
   * Return the number of off-package credits.
   * Revelevant only for Subscription tier.
   */
  getOffPackageCreditCount: (
    companyConsumption: CompanyConsumption | undefined
  ) => {
    if (
      companyConsumption &&
      TierType.Subscription == AccountUtil.getCompanyTier(companyConsumption) &&
      companyConsumption.company_balance &&
      companyConsumption.company_balance < 0
    ) {
      return -companyConsumption.company_balance
    }

    return 0
  },

  /**
   * Return the number of paid credits.
   *
   * Paid credits are credits coming from a subscription.
   * If the subscription is canceled, the remaining paid credits are still
   * available.
   */
  getPaidCreditCount: (companyConsumption: CompanyConsumption | undefined) => {
    if (
      companyConsumption?.company_balance &&
      companyConsumption?.company_balance > 0
    ) {
      return companyConsumption?.company_balance
    }

    return 0
  },

  /**
   * Return the number of consumed credits within the credit count of the plan.
   * In case of Subscription plan, the off-package credit is not counted.
   */
  getPlanCreditCount: (companyConsumption: CompanyConsumption | undefined) => {
    const allCreditCount = AccountUtil.getConsumption(companyConsumption)

    return TierType.Subscription ==
      AccountUtil.getCompanyTier(companyConsumption)
      ? Math.min(
          allCreditCount,
          AccountUtil.getConsumptionMaxCount(companyConsumption)
        )
      : allCreditCount
  },

  getConsumptionMaxCount: (
    companyConsumption: CompanyConsumption | undefined,
    invoice?: any | undefined
  ) => {
    const currentTier = AccountUtil.getCompanyTier(companyConsumption)

    switch (currentTier) {
      case TierType.Free:
        return companyConsumption?.start_voucher_balance &&
          companyConsumption?.start_voucher_balance > 0
          ? companyConsumption?.start_voucher_balance
          : TierFreeCreditMaxCount

      case TierType.PayAsYouGo:
        return invoice?.starting_balance ?? 0

      case TierType.Subscription:
        return companyConsumption?.current_plan

      default:
        return 0
    } // switch
  },

  /**
   * Return the label of an invoice returned by the backend.
   * @param invoicePlan The invoice, containing the array `lines.data`.
   * @returns The type of invoice.
   */
  getInvoicePlanLabel(apiPlanInvoice: any) {
    return apiPlanInvoice.lines.data[0].price.lookup_key
  },

  /**
   * Return the type of invoice, given the label of the invoice's plan.
   * @param invoicePlan The label of the plan returned by the backend.
   * @returns The type of invoice.
   */
  invoiceLabelToType: (invoicePlan: string | undefined): InvoiceType => {
    switch (invoicePlan) {
      case 'payg_eur_2':
      case 'payg_usd_2':
        return InvoiceType.PayAsYouGo

      case 'sub_starter_monthly_over':
        return InvoiceType.StarterMonthlyOverPackage

      case 'sub_starter_monthly_base':
        return InvoiceType.StarterMonthlyBase

      case 'sub_starter_yearly_over':
        return InvoiceType.StarterYearlyOverPackage

      case 'sub_starter_yearly_base':
        return InvoiceType.StarterYearlyBase

      case 'sub_pro_monthly_over':
        return InvoiceType.ProMonthlyOverPackage

      case 'sub_pro_monthly_base':
        return InvoiceType.ProMonthlyBase

      case 'sub_pro_yearly_over':
        return InvoiceType.ProYearlyOverPackage

      case 'sub_pro_yearly_base':
        return InvoiceType.ProYearlyBase

      default:
        return InvoiceType.Unknown
    } // switch
  },

  /**
   * Return the type of invoice, given an invoice returned by the backend.
   * @param invoicePlan The invoice, containing the array `lines.data`.
   * @returns The type of invoice.
   */
  invoiceToType: (invoice: any | undefined): InvoiceType => {
    const planLabel = AccountUtil.getInvoicePlanLabel(invoice)
    return AccountUtil.invoiceLabelToType(planLabel)
  },
} // AccountUtil
